// src/pages/EventListPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "api";
import { Preferences } from "@capacitor/preferences";
import BackButton from "../components/BackButton";
import { MapPin } from "phosphor-react";
import LocationAutoComplete from "../components/LocationAutoComplete";
import DateTimeField12h from "../components/DateTimeField12h";

/* -------- iOS-safe auth helpers -------- */
async function getToken() {
  try {
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref && pref.value) return pref.value;
  } catch {}
  return localStorage.getItem("token") || sessionStorage.getItem("token") || null;
}

async function getAuthHeaders() {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/* -------- Local "YYYY-MM-DDTHH:MM" from ISO-ish input -------- */
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${mi}`;
}

export default function EventListPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightEventId = searchParams.get("event");

  const [events, setEvents] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);

  const [manage, setManage] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("future");

  const { futureEvents, pastEvents } = useMemo(() => {
    const now = Date.now();
    const deduped = [];
    const seen = new Set();

    events.forEach((e) => {
      if (!e?.id || !e?.start_time) return;
      if (seen.has(e.id)) return;
      seen.add(e.id);
      deduped.push(e);
    });

    return {
      futureEvents: deduped
        .filter((e) => new Date(e.start_time).getTime() >= now)
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time)),
      pastEvents: deduped
        .filter((e) => new Date(e.start_time).getTime() < now)
        .sort((a, b) => new Date(b.start_time) - new Date(a.start_time)),
    };
  }, [events]);

  const visibleEvents = tab === "future" ? futureEvents : pastEvents;

  const seedDrafts = (evs) => {
    const init = {};
    evs.forEach((e) => {
      init[e.id] = {
        title: e.title || "",
        start_time: toLocalInput(e.start_time) || "",
        location_text: e.location_text || "",
        location_url: e.location_url || "",
        description: e.description || "",
      };
    });
    setDrafts(init);
  };

  const refreshEvents = async () => {
    try {
      const headers = await getAuthHeaders();
      const evRes = await api.get(`/groups/${id}/events?include_past=true&limit=200`, {
        headers,
      });
      const evs = Array.isArray(evRes?.data) ? evRes.data : [];
      setEvents(evs);
      seedDrafts(evs);
    } catch {}
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        const headers = await getAuthHeaders();

        const evRes = await api.get(`/groups/${id}/events?include_past=true&limit=200`, {
          headers,
        });
        const evs = Array.isArray(evRes?.data) ? evRes.data : [];

        if (!mounted) return;

        setEvents(evs);
        seedDrafts(evs);

        try {
          const [meRes, adminRes] = await Promise.all([
            api.get("/users/me", { headers }),
            api.get(`/groups/${id}/admins`, { headers }).catch(() => ({ data: [] })),
          ]);

          const meId = meRes?.data?.id;
          const admins = Array.isArray(adminRes?.data) ? adminRes.data : [];

          if (mounted) {
            setIsAdmin(admins.some((a) => String(a.id) === String(meId)));
          }
        } catch {
          if (mounted) setIsAdmin(false);
        }
      } catch {
        if (mounted) {
          setEvents([]);
          setDrafts({});
          setIsAdmin(false);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (!loading && futureEvents.length === 0 && pastEvents.length > 0) {
      setTab("past");
    }
  }, [loading, futureEvents.length, pastEvents.length]);

  const onEdit = (eid) => setEditingId(eid);
  const onCancel = () => setEditingId(null);

  const updateDraft = (eid, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [eid]: {
        ...(prev[eid] || {}),
        ...patch,
      },
    }));
  };

  const onDelete = async (eid) => {
    if (!window.confirm("Delete this event? This cannot be undone.")) return;

    try {
      setSavingId(eid);
      const headers = await getAuthHeaders();
      await api.delete(`/groups/events/${eid}`, { headers });
      setSavingId(null);
      await refreshEvents();
    } catch {
      setSavingId(null);
    }
  };

  const onSave = async (eid) => {
    const d = drafts[eid] || {};

    const payload = {
      title: (d.title || "").trim(),
      start_time: d.start_time ? new Date(d.start_time).toISOString() : null,
      description: d.description?.trim() || null,
      location_text: d.location_text?.trim() || null,
      location_url: d.location_url?.trim() || null,
    };

    try {
      setSavingId(eid);
      const headers = await getAuthHeaders();
      await api.patch(`/groups/events/${eid}`, payload, { headers });
      setSavingId(null);
      setEditingId(null);
      await refreshEvents();
    } catch {
      setSavingId(null);
    }
  };

  useEffect(() => {
    if (!highlightEventId || events.length === 0) return;

    const ev = events.find(
      (e) => String(e.id) === String(highlightEventId)
    );

    if (!ev) return;

    setTab(
      new Date(ev.start_time) >= new Date()
        ? "future"
        : "past"
    );
  }, [highlightEventId, events]);

  return (
    <div className="app-shell bg-blue-100 min-h-[100svh]">
      <header
        className="fixed top-0 left-0 right-0 z-30 bg-blue-50 backdrop-blur"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="h-12 max-w-5xl mx-auto px-4 flex items-center">
          <BackButton
            className="text-blue-600"
            ariaLabel="Back"
            onClick={() => navigate(-1)}
          />

          <h1 className="text-blue-600 font-extrabold text-lg">Events</h1>
        </div>
      </header>

      <div style={{ height: "calc(48px + env(safe-area-inset-top))" }} />

      <main
        className="app-content px-4 pt-8 py-8"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 64px)" }}
      >
        {loading ? (
          <div className="p-6 text-sm text-gray-600">Loading…</div>
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="inline-flex rounded-full bg-blue-200 p-1">
                <button
                  type="button"
                  onClick={() => setTab("future")}
                  className={[
                    "!h-8 !min-h-0 rounded-full px-3 text-sm font-medium transition",
                    tab === "future"
                      ? "bg-blue-600 text-white"
                      : "text-blue-600 hover:bg-white",
                  ].join(" ")}
                >
                  Upcoming Events
                  {futureEvents.length > 0 ? ` (${futureEvents.length})` : ""}
                </button>

                <button
                  type="button"
                  onClick={() => setTab("past")}
                  className={[
                    "!h-8 !min-h-0 rounded-full px-3 text-sm font-semibold transition",
                    tab === "past"
                      ? "bg-blue-600 text-white"
                      : "text-blue-600 hover:bg-white",
                  ].join(" ")}
                >
                  Past Events
                  {pastEvents.length > 0 ? ` (${pastEvents.length})` : ""}
                </button>
              </div>
            </div>


            {isAdmin && (
              <div className="mb-2 text-end">
                <button
                  type="button"
                  onClick={() => setManage((m) => !m)}
                  className="text-[13px] font-medium px-3 !min-h-0 !h-7 rounded-full bg-blue-700 text-white"
                >
                  {manage ? "Done" : "Manage"}
                </button>
              </div>
            )}

            {visibleEvents.length === 0 ? (
              <div className="px-4 py-2 text-sm font-semibold text-blue-500">
                {tab === "future" ? "No upcoming events" : "No past events"}
              </div>
            ) : (
              <div className="space-y-2.5">
                {visibleEvents.map((ev) => {
                  const isEditing = editingId === ev.id;
                  const row = drafts[ev.id] || {};
                  const dateObj = new Date(ev.start_time);
                  const canSave = row.title?.trim() && row.start_time;

                  return (
                    <div
                      key={ev.id}
                      className={[
                        "rounded-3xl bg-blue-100 px-4 pr-5 py-4",
                        String(ev.id) === String(highlightEventId)
                          ? "border-2 border-blue-600"
                          : "",
                      ].join(" ")}
                    >
                      <div className="flex items-start gap-5">
                        {!isEditing && (
                          <div className="shrink-0 rounded-2xl bg-white px-3 py-3 shadow-[0px_4px_0px_rgba(37,99,235,0.96)] text-center w-14">
                            <div className="text-lg font-black -mb-1.5 text-amber-500">
                              {String(dateObj.getDate())}
                            </div>
                            <div className="text-sm font-bold text-gray-500">
                              {dateObj
                                .toLocaleString("en-US", { month: "short" })
                                .toUpperCase()}
                            </div>
                          </div>
                        )}

                        <div className="min-w-0 flex-1">
                          {!isEditing ? (
                            <>
                              <div className="text-sm font-medium leading-tight text-gray-600">
                                {dateObj.toLocaleString("en-US", {
                                  weekday: "short",
                                  hour: "numeric",
                                  minute: "2-digit",
                                })}
                              </div>

                              <div className="sm:text-[16px] text-sm font-bold leading-snug break-words text-blue-900">
                                {ev.title}
                              </div>

                              {(ev.location_text || ev.location_url) && (
                                <div className="mt-1 flex items-start gap-0.5 text-xs text-blue-600 break-words">
                                  <MapPin
                                    size={12}
                                    weight="fill"
                                    className="shrink-0 text-blue-600"
                                  />

                                  {ev.location_url ? (
                                    <a
                                      href={ev.location_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-600 leading-none break-words"
                                    >
                                      {ev.location_text ||
                                        ev.location_url.replace(/^https?:\/\//, "")}
                                    </a>
                                  ) : (
                                    <span>{ev.location_text}</span>
                                  )}
                                </div>
                              )}

                              {ev.description && (
                                <div className="mt-1.5 text-sm leading-5 text-blue-900/75 whitespace-pre-wrap break-words">
                                  {ev.description}
                                </div>
                              )}
                            </>
                          ) : (
                            <form
                              onSubmit={(e) => {
                                e.preventDefault();
                                if (canSave && savingId !== ev.id) onSave(ev.id);
                              }}
                              className="space-y-3"
                            >
                              <div>
                                <label className="block text-sm font-medium mb-1 ml-3">
                                  Event Name
                                </label>
                                <input
                                  type="text"
                                  value={row.title || ""}
                                  onChange={(e) =>
                                    updateDraft(ev.id, { title: e.target.value })
                                  }
                                  className="w-full border rounded-2xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                                  placeholder="Cooking Class @Sur La Table"
                                  required
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium mb-1 ml-3">
                                  Date & Time
                                </label>
                                <DateTimeField12h
                                  value={row.start_time || ""}
                                  onChange={(val) =>
                                    updateDraft(ev.id, { start_time: val })
                                  }
                                  minuteStep={5}
                                  className="w-full text-sm"
                                  wideDate
                                />
                              </div>

                              <div>
                                <label className="block text-sm ml-3 font-medium mb-1">
                                  Location
                                </label>
                                <div className="relative z-[1000] overflow-visible">
                                  <LocationAutoComplete
                                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                                    value={row.location_text || ""}
                                    onChange={(val) =>
                                      updateDraft(ev.id, { location_text: val })
                                    }
                                    inputClassName="py-1"
                                    placeholder=""
                                    country="us"
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm ml-3 font-medium mb-1">
                                  Description
                                </label>
                                <textarea
                                  rows={4}
                                  value={row.description || ""}
                                  onChange={(e) =>
                                    updateDraft(ev.id, {
                                      description: e.target.value,
                                    })
                                  }
                                  className="w-full border rounded-2xl p-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium ml-3 mb-1">
                                  Online Link
                                </label>
                                <input
                                  type="url"
                                  value={row.location_url || ""}
                                  onChange={(e) =>
                                    updateDraft(ev.id, {
                                      location_url: e.target.value,
                                    })
                                  }
                                  placeholder="Zoom/Meet link or any URL"
                                  className="w-full border rounded-2xl p-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                                />
                              </div>

                              <div className="flex justify-end gap-2 pt-2">
                                <button
                                  type="button"
                                  onClick={onCancel}
                                  disabled={savingId === ev.id}
                                  className="bg-white text-blue-800 text-sm rounded-full px-3 !min-h-0 !h-7 disabled:opacity-50"
                                >
                                  Cancel
                                </button>

                                <button
                                  type="submit"
                                  disabled={!canSave || savingId === ev.id}
                                  className="bg-blue-600 text-white text-sm rounded-full px-3 !min-h-0 !h-7 disabled:opacity-50"
                                >
                                  {savingId === ev.id ? "Saving…" : "Save"}
                                </button>
                              </div>
                            </form>
                          )}
                        </div>

                        {isAdmin && manage && !isEditing && (
                          <div className="shrink-0 flex flex-col items-end">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 rounded-full bg-transparent px-2.5 !min-h-0 !h-5 text-xs font-medium text-blue-800 hover:font-bold"
                              onClick={() => onEdit(ev.id)}
                              title="Edit event"
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              className="inline-flex items-center rounded-full bg-transparent px-2.5 !min-h-0 !h-5 text-xs font-medium text-red-700 transition disabled:opacity-75 hover:font-bold"
                              onClick={() => onDelete(ev.id)}
                              disabled={savingId === ev.id}
                              title="Delete event"
                            >
                              {savingId === ev.id ? "Deleting…" : "Delete"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}